import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  defaultSystemSettings,
  getSystemSettings,
  updateSystemSettings,
  type MaintenanceSettings,
  type SystemSettings,
} from '@/lib/api/systemSettings';

interface SystemSettingsContextValue {
  settings: SystemSettings;
  loaded: boolean;
  refresh: () => Promise<void>;
  saveMaintenanceSettings: (maintenance: Partial<MaintenanceSettings>) => Promise<SystemSettings>;
}

const SystemSettingsContext = createContext<SystemSettingsContextValue | null>(null);

export function SystemSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SystemSettings>(defaultSystemSettings);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const next = await getSystemSettings();
      setSettings(next);
    } catch {
      setSettings((current) => current || defaultSystemSettings);
    } finally {
      setLoaded(true);
    }
  }, []);

  const saveMaintenanceSettings = useCallback(async (maintenance: Partial<MaintenanceSettings>) => {
    const next = await updateSystemSettings(maintenance);
    setSettings(next);
    setLoaded(true);
    return next;
  }, []);

  useEffect(() => {
    void refresh();

    const intervalId = window.setInterval(() => {
      void refresh();
    }, 30000);

    const handleFocus = () => {
      void refresh();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refresh();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refresh]);

  const value = useMemo(
    () => ({
      settings,
      loaded,
      refresh,
      saveMaintenanceSettings,
    }),
    [loaded, refresh, saveMaintenanceSettings, settings],
  );

  return (
    <SystemSettingsContext.Provider value={value}>
      {children}
    </SystemSettingsContext.Provider>
  );
}

export function useSystemSettings() {
  const ctx = useContext(SystemSettingsContext);
  if (!ctx) {
    throw new Error('useSystemSettings must be used within SystemSettingsProvider');
  }

  return ctx;
}
